import { useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import { Box, CircularProgress } from "@mui/material";

import ImgBg from "../../assets/images/indexes/bg-material.svg";
import { useTranslation } from "../../hooks/useTranslation";
import { CONSTITUENTS_FILE_TYPE, INDEX_HISTORY_CSV_FILE_TYPE } from "../../pages/IndexDetails/config";
import { getConstituentsFile, getPdfFile } from "../../services";
import { downloadFileInBrowser } from "../../utils";
import { TranslationKey } from "../../utils/translations";
import { Container, Background, Title, Content, Type } from "./styles";

/**
 * Get translated file type name
 */
const getFileTypeTranslation = (
  type: string,
  t: (key: TranslationKey) => string
): string => {
  // Map file type to translation key
  const typeMap: Record<string, TranslationKey> = {
    factsheet: "factsheet",
    methodology_guidebook: "methodologyGuidebook",
    constituents_list: "constituentsList",
    corporate_actions: "corporateActions",
    benchmark_statement: "benchmarkStatement",
    index_history: "indexHistory",
    "Rebalance Constituents": "rebalanceConstituents", // Custom button name
  };

  const translationKey = typeMap[type];
  if (translationKey) {
    return t(translationKey);
  }

  // Fallback to formatted type name if no translation found
  return type?.split("_").join(" ") || type;
};

interface Props {
  type: string;
  url: string;
  background: string;
  fileIndex: number;
  fileName: string;
  indexId: string;
  fileShouldBeDownloadable: string;
  isCustomButton: boolean;
}

const BCAUEE_CUSTOM_URL =
  "https://cedarportfolio.com/main/wp-content/uploads/2021/12/The-Cedar-Austrian-Economics-ESG-Index-Investment-Strategy-Latest.pdf"; // TODO: It is hardcoded for now due to high priority, in a next iteration it will be removed and certain features will be made @andrea_da_silva @paul_herrera [IT-14218]
const BCAUEE_INDEX = "BCAUEE";

const Material: React.FC<Props> = ({
  type,
  url,
  background,
  fileIndex,
  fileName,
  indexId,
  fileShouldBeDownloadable,
  isCustomButton,
}) => {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState<boolean>(false);

  const downloadPDF = async (url: string, filename: string) => {
    try {
      setDownloading(true);
      const file = await getPdfFile(url);
      downloadFileInBrowser(file, filename, false, "pdf");
      setDownloading(false);
    } catch (err) {
      setDownloading(false);
      console.info("error fetching the pdf document to download", err);
    }
  };

  const downloadOrLinkSwitcher = async (url: string, fileName: string) => {
    isCustomButton && fileShouldBeDownloadable === "A"
      ? await downloadPDF(url, fileName)
      : window.open(url, "_blank");
  };

  const handleOpenPdf = async () => {
    if (indexId === BCAUEE_INDEX) {
      // TODO: It is hardcoded for now due to high priority, in a next iteration it will be removed and certain features will be made @andrea_da_silva @paul_herrera [IT-14218]
      await downloadOrLinkSwitcher(BCAUEE_CUSTOM_URL, fileName);
    } else if (url) {
      await downloadOrLinkSwitcher(url, fileName);
    }
  };

  const handleDownloadConstituentsFile = async () => {
    try {
      if (fileIndex && !downloading) {
        setDownloading(true);
        const file = await getConstituentsFile(indexId);
        const csvFileName = fileName.replace(/\.xlsx$/i, ".csv");
        downloadFileInBrowser(file, csvFileName, false, "csv");
        setDownloading(false);
      }
    } catch (error) {
      setDownloading(false);
    }
  };

  const handleDownloadCsvFile = () => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  const handleClick = () => {
    if (type === CONSTITUENTS_FILE_TYPE) {
      handleDownloadConstituentsFile();
    } else if (type === INDEX_HISTORY_CSV_FILE_TYPE) {
      handleDownloadCsvFile();
    } else {
      handleOpenPdf();
    }
  };

  return (
    <Container
      sx={{
        background,
      }}
      onClick={handleClick}
    >
      <Background sx={{ backgroundImage: `url(${ImgBg})` }}></Background>
      <Title>{t("indexResources")}</Title>
      <Content>
        <Type>{getFileTypeTranslation(type, t)}</Type>
        <Box>
          {downloading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <DownloadIcon />
          )}
        </Box>
      </Content>
    </Container>
  );
};

export default Material;
